import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { Flag, X } from "lucide-react";
import { toast } from "sonner";

const REASONS = [
  { id: "harassment", label: "Harassment or bullying" },
  { id: "hate_speech", label: "Hate speech" },
  { id: "self_harm", label: "Self-harm or suicide" },
  { id: "violence", label: "Violence or threats" },
  { id: "sexual_content", label: "Sexual content" },
  { id: "spam", label: "Spam or scams" },
  { id: "impersonation", label: "Impersonation" },
  { id: "misinformation", label: "Misinformation" },
  { id: "other", label: "Something else" },
];

export default function ReportDialog({ open, onClose, targetType, targetId, targetLabel }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setNote("");
      return undefined;
    }

    if (typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscroll;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const submit = async () => {
    if (!reason) return toast.error("Pick a reason");
    setBusy(true);
    try {
      await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason,
        note,
      });
      toast.success("Report submitted — thank you for helping keep HAEVEN safe");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send report");
    } finally {
      setBusy(false);
    }
  };

  const dialog = (
    <div
      data-testid="report-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-dialog-title"
      className="fixed inset-0 z-[9999] flex items-stretch sm:items-center justify-center sm:p-4 overflow-hidden"
    >
      <button
        type="button"
        aria-label="Close report dialog"
        className="absolute inset-0 bg-card sm:bg-foreground/50"
        onClick={onClose}
      />
      <div className="relative z-10 bg-card w-full h-full min-h-0 sm:h-auto sm:max-w-md sm:max-h-[85vh] flex flex-col overflow-hidden sm:brutal-card">
        <div className="flex items-start justify-between gap-3 px-4 sm:px-6 pb-3 pt-[max(1rem,env(safe-area-inset-top))] sm:pt-6 shrink-0 border-b border-foreground/10 bg-card">
          <div>
            <h3 id="report-dialog-title" className="text-2xl flex items-center gap-2">
              <Flag size={20} strokeWidth={2.5} /> Report
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {targetLabel || `Reporting this ${targetType}`}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close report dialog"
            className="brutal-btn p-2 bg-card shrink-0"
            data-testid="report-dialog-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-scroll overscroll-contain px-4 sm:px-6 py-4 touch-pan-y [-webkit-overflow-scrolling:touch]">
          <p className="font-semibold mb-3">Choose a reason</p>
          <div className="space-y-2">
            {REASONS.map((r) => (
              <label
                key={r.id}
                className={`brutal-card flex items-center gap-3 p-3 cursor-pointer ${reason === r.id ? "bg-accent" : "bg-card"}`}
              >
                <input
                  type="radio"
                  name="reason"
                  value={r.id}
                  checked={reason === r.id}
                  onChange={() => setReason(r.id)}
                  data-testid={`report-reason-${r.id}`}
                />
                <span className="text-sm">{r.label}</span>
              </label>
            ))}
          </div>

          <label className="block font-semibold mt-5 mb-2" htmlFor="report-note">
            Optional context
          </label>
          <textarea
            id="report-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add context if helpful (max 500 chars)"
            maxLength={500}
            rows={3}
            className="brutal-input w-full px-3 py-2 text-sm"
            data-testid="report-note"
          />
          <div className="h-4" aria-hidden="true" />
        </div>

        <div className="shrink-0 border-t border-foreground/10 bg-card px-4 sm:px-6 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex gap-2">
          <button onClick={onClose} className="brutal-btn flex-1 px-4 py-3 bg-card">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !reason}
            className="brutal-btn flex-1 px-4 py-3 bg-primary text-primary-foreground disabled:opacity-50"
            data-testid="report-submit-btn"
          >
            {busy ? "Sending…" : "Submit report"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}
